import { App, Button, Flex, Table } from 'antd';
import { FC, memo, useCallback, useEffect, useState } from 'react';
import {
  DeleteOutlined,
  EyeOutlined,
  ShareAltOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { deleteDicomShare, fetchDicomShares } from 'services/api/dicomShares';
import { DicomShareRow } from 'types/api/dicomShares';

import { showErrorMessage } from 'utils/showErrorMessage';
import { getFileUrl } from 'utils/file';
import useCopyText from 'hooks/useCopyText';

const PAGE_LIMIT = 10;

interface IProps {
  refreshSignal?: number;
}

const DicomList: FC<IProps> = ({ refreshSignal = 0 }) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);

  const { copyText } = useCopyText();

  const { modal, message } = App.useApp();

  const [results, setResults] = useState<DicomShareRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const res = await fetchDicomShares({ page, limit: PAGE_LIMIT });
        if (cancelled) return;
        setResults(res.results);
        setTotal(res.total);
      } catch (err) {
        if (!cancelled) showErrorMessage(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [page, refreshKey, refreshSignal]);

  const onClickDelete = (id: number) => {
    modal.confirm({
      title: 'Warning',
      content: <p>Are you sure?</p>,
      async onOk() {
        try {
          await deleteDicomShare(id);
          setRefreshKey(k => k + 1);
        } catch (err) {
          showErrorMessage(err);
        }
      }
    });
  };

  const onClickShare = useCallback(
    (link: string) => {
      copyText(link);
      message.success(t('messages.copied'));
    },
    [copyText, message, t]
  );

  return (
    <Table
      dataSource={results}
      columns={[
        {
          title: 'ID',
          dataIndex: 'id',
          key: 'id'
        },
        {
          title: 'Title',
          dataIndex: 'name',
          key: 'name',
          render(value) {
            return value || '-';
          }
        },
        {
          title: 'File',
          dataIndex: 'file',
          key: 'file',
          render(value) {
            return value ? (
              <a
                download={value}
                target="_blank"
                rel="noreferrer"
                href={getFileUrl(value)}
              >
                {value}
              </a>
            ) : (
              '-'
            );
          }
        },
        {
          title: '',
          dataIndex: 'id',
          key: 'actions',
          width: '5%',
          align: 'right',
          render: (id, record) => {
            const link = `${record.redirect_url}?sessionId=${record.session_id}`;

            return (
              <Flex align="center" gap={16}>
                <Button
                  icon={<DeleteOutlined />}
                  type="text"
                  onClick={() => onClickDelete(id)}
                  danger
                >
                  {t('table.delete')}
                </Button>
                <Button
                  icon={<ShareAltOutlined />}
                  onClick={() => onClickShare(link)}
                  // onClick={() => setIsOpenModal(true)}
                >
                  Share
                </Button>
                <a target="_blank" href={link} rel="noreferrer">
                  <Button icon={<EyeOutlined />} type="primary" ghost>
                    {t('sessions.view_dicom')}
                  </Button>
                </a>
              </Flex>
            );
          }
        }
      ]}
      loading={loading}
      rowKey="id"
      pagination={{
        total,
        pageSize: PAGE_LIMIT,
        onChange: p => setPage(p),
        showSizeChanger: false
      }}
    />
  );
};

export default memo(DicomList);
